/**
 * Phase 14.5 Step 3 — IMU sub-form (controlled value/onChange leaf).
 *
 * Registry-driven via the `imu.fields` catalogue (Phase 14 backend
 * `/api/degradation-profiles/registry`). Six fields:
 *
 *   tuple3 — gyro_bias_dps, gyro_drift_dps_per_s,
 *            accel_bias_mps2, accel_drift_mps2_per_s
 *   scalar — sample_rate_hz (>0, ≤10000), seed (int)
 *
 * Held to the Step-2 sub-form invariant: NO internal `useForm`. The
 * parent (DegradationProfileEditor) is the single source of truth.
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function _setField(value, field, next, onChange) {
    onChange?.({ ...(value || {}), [field]: next });
}

function _setTuple(value, field, idx, raw, onChange) {
    const arr = Array.isArray(value?.[field]) ? [...value[field]] : [0, 0, 0];
    arr[idx] = parseFloat(raw);
    if (!Number.isFinite(arr[idx])) arr[idx] = 0;
    _setField(value, field, arr, onChange);
}

function TupleField({ id, label, unit, hint, value, onChange, parent, parentField }) {
    const tuple = Array.isArray(value) ? value : [0, 0, 0];
    return (
        <div className="space-y-1" data-testid={`imu-field-${id}`}>
            <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                {label} <span className="text-ux-text-3/60">({unit})</span>
            </Label>
            <div className="grid grid-cols-3 gap-2">
                {["x", "y", "z"].map((axis, i) => (
                    <Input
                        key={axis}
                        type="number"
                        step="any"
                        value={tuple[i] ?? 0}
                        onChange={(e) => _setTuple(parent, parentField, i, e.target.value, onChange)}
                        data-testid={`imu-${id}-${axis}`}
                        aria-label={`${label} ${axis}`}
                    />
                ))}
            </div>
            {hint && <p className="text-[11px] text-ux-text-3">{hint}</p>}
        </div>
    );
}

export default function IMUSubForm({ value, onChange, registry }) {
    // Registry shape: { fields: [{id, shape, unit, ...}, ...] }
    const fields = Array.isArray(registry?.fields) ? registry.fields : [];
    const byId = Object.fromEntries(fields.map((f) => [f.id, f]));

    // Phase 14.5 Step 6 (Y2a) — registry-driven dispatch on field
    // shape, replacing the prior 4-id hardcoded `tupleFields`
    // constant. Any field with `shape === "tuple3"` renders the
    // 3-axis input row; scalar shapes render below. The order is
    // preserved from the registry payload so the backend remains the
    // single source of truth for IMU field identity AND ordering.
    const tupleFields = fields.filter((f) => f?.shape === "tuple3").map((f) => f.id);
    const scalarFields = fields.filter((f) => f?.shape !== "tuple3");

    return (
        <div className="space-y-4" data-testid="imu-subform">
            {tupleFields.map((fid) => {
                const meta = byId[fid];
                if (!meta) return null;
                return (
                    <TupleField
                        key={fid}
                        id={fid}
                        label={fid}
                        unit={meta.unit}
                        hint={meta.description}
                        value={value?.[fid]}
                        onChange={onChange}
                        parent={value}
                        parentField={fid}
                    />
                );
            })}

            <div className="grid grid-cols-2 gap-3">
                {byId.sample_rate_hz && (
                    <div data-testid="imu-field-sample_rate_hz">
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            sample_rate_hz <span className="text-ux-text-3/60">(Hz)</span>
                        </Label>
                        <Input
                            type="number"
                            step="any"
                            min={byId.sample_rate_hz?.physical_bounds?.lo}
                            max={byId.sample_rate_hz?.physical_bounds?.hi}
                            value={value?.sample_rate_hz ?? 0}
                            onChange={(e) => _setField(value, "sample_rate_hz", parseFloat(e.target.value || "0"), onChange)}
                            data-testid="imu-sample-rate-hz"
                        />
                    </div>
                )}
                {byId.seed && (
                    <div data-testid="imu-field-seed">
                        <Label className="block text-xs font-medium uppercase tracking-wide text-ux-text-3">
                            seed
                        </Label>
                        <Input
                            type="number"
                            step="1"
                            value={value?.seed ?? 0}
                            onChange={(e) => _setField(value, "seed", parseInt(e.target.value || "0", 10) || 0, onChange)}
                            data-testid="imu-seed"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
